import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import BingoCardsGame from './BingoCardsGame';
import './BingoCards.css';

const PHASES = {
  ENTRY: 'entry', // Enter card numbers (no auth needed)
  GAME: 'game',   // View bingo card grids
};

// Card set id -> display label. Ids match the backend / bundled JSON filenames.
const SET_LABELS = {
  'default': 'Regular',
  'power-1': 'Power 1',
};

const BingoCardsPage = () => {
  const [searchParams] = useSearchParams();
  const cardSet = searchParams.get('cardSet') || 'default';
  const setLabel = SET_LABELS[cardSet] || cardSet;

  const [phase, setPhase] = useState(PHASES.ENTRY);
  const [selectedCardNumbers, setSelectedCardNumbers] = useState([]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOnlineToast, setShowOnlineToast] = useState(false);

  // Card entry state
  const [cardCount, setCardCount] = useState(1);
  const [cardInputs, setCardInputs] = useState(['', '', '', '', '', '', '', '', '', '', '', '']);
  const [entryError, setEntryError] = useState('');

  // Network status detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOnlineToast(true);
      setTimeout(() => setShowOnlineToast(false), 3000);
    };
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // ===== Card Entry Handlers =====
  const handleCardInputChange = useCallback((index, value) => {
    // Only allow digits
    const cleaned = value.replace(/\D/g, '');
    setCardInputs(prev => {
      const next = [...prev];
      next[index] = cleaned;
      return next;
    });
  }, []);

  const handleGenerateCards = useCallback(() => {
    setEntryError('');

    const numbers = cardInputs
      .slice(0, cardCount)
      .map(s => parseInt(s.trim(), 10))
      .filter(n => !isNaN(n) && n >= 1);

    if (numbers.length === 0) {
      setEntryError('Enter at least one card number');
      return;
    }

    setSelectedCardNumbers(numbers);
    setPhase(PHASES.GAME);
  }, [cardInputs, cardCount]);

  const handleBack = useCallback(() => {
    setPhase(PHASES.ENTRY);
  }, []);

  return (
    <div className="bc-app">
      {!isOnline && (
        <div className="bc-network-bar offline">No Internet Connection</div>
      )}
      {showOnlineToast && (
        <div className="bc-network-bar online">Back Online</div>
      )}

      {/* DEFAULT: Card Entry Screen (no auth needed) */}
      {phase === PHASES.ENTRY && (
        <div className="bc-entry">
          <div className="bc-entry-card">
            <h1 className="bc-entry-title">Bingo Cards</h1>
            <p className="bc-entry-subtitle">
              Enter your card numbers to display them ({setLabel})
            </p>

            {/* Card count selector */}
            <div className="bc-entry-count">
              <label>Number of cards</label>
              <select
                className="bc-entry-select"
                value={cardCount}
                onChange={(e) => setCardCount(Number(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(n => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>

            {/* Card number inputs */}
            <div className="bc-entry-inputs">
              <label>Enter card numbers</label>
              <div className="bc-entry-inputs-grid">
                {Array.from({ length: cardCount }, (_, i) => (
                  <input
                    key={i}
                    type="text"
                    inputMode="numeric"
                    placeholder={`Card ${i + 1}`}
                    value={cardInputs[i]}
                    onChange={(e) => handleCardInputChange(i, e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleGenerateCards()}
                    className="bc-entry-input"
                    maxLength={4}
                  />
                ))}
              </div>
            </div>

            {entryError && (
              <div className="bc-entry-error">{entryError}</div>
            )}

            <button className="bc-entry-btn primary" onClick={handleGenerateCards}>
              Show Cards
            </button>
          </div>
        </div>
      )}

      {/* Bingo Card Viewer */}
      {phase === PHASES.GAME && (
        <BingoCardsGame
          cardNumbers={selectedCardNumbers}
          cardSet={cardSet}
          onBack={handleBack}
        />
      )}
    </div>
  );
};

export default BingoCardsPage;
